import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WORKFLOWSTART_SUCCESS } from 'actions/payway';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { getPayPalClientToken } from 'services/api/payway/paypalClientToken';
import { getSyncClient } from 'services/api/payway/syncClient';
import { getCurrentPayment } from 'containers/Payment/Checkout/utils/getCurrentPayment';
import { setCurrentWFS } from 'action-creators/payway/currentPayment/currentWFS';
import { dismissIOSKeyboard } from 'utils/dismissIOSKeyboard';

const PAYPAL_CONFIG_KEY = 'paypalgate_configuration';
const PAYPAL_SDK_SCRIPT_ID = 'paypal-jsv6-sdk';

const loadPayPalScript = url =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(PAYPAL_SDK_SCRIPT_ID);
    if (existing) {
      if (window.paypal) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('No se pudo cargar el SDK de PayPal')),
      );
      return;
    }

    const script = document.createElement('script');
    script.id = PAYPAL_SDK_SCRIPT_ID;
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('No se pudo cargar el SDK de PayPal'));
    document.body.appendChild(script);
  });

const usePayPalSDK = ({
  goToScreen,
  searchParam,
  standalone = false,
  userId: userIdOverride,
  userToken,
  transactionParams,
  onVaultSuccess,
} = {}) => {
  const dispatch = useDispatch();
  const paypalConfig = useSelector(
    selectMetadataKeyByRegion(PAYPAL_CONFIG_KEY),
  );
  const reduxUserId = useSelector(state => state.user?.user_id);
  const userId = userIdOverride || reduxUserId;
  const currentPayment = useSelector(state => state.payway?.currentPayment);
  const isMountedRef = useRef(false);
  const saveSessionRef = useRef(null);
  const syncPayPalVaultRef = useRef(null);
  const startVaultSessionRef = useRef(null);

  // Refs para valores mutables que syncPayPalVault necesita —
  // así el useCallback puede tener deps [] y no recrearse cada
  // vez que cambia currentPayment (lo cual re-crearía la
  // saveSession del SDK y produce pantalla negra).
  const currentPaymentRef = useRef(currentPayment);
  useEffect(
    () => {
      currentPaymentRef.current = currentPayment;
    },
    [currentPayment],
  );

  const standaloneRef = useRef(standalone);
  useEffect(
    () => {
      standaloneRef.current = standalone;
    },
    [standalone],
  );

  // El vault de QRLinking se autentica con el user_token de link-sessions/link.
  // Va por ref porque quien dispara sync-client es el onApprove del SDK, no
  // nuestro código: en ese momento no hay forma de pasarlo como argumento.
  const userTokenRef = useRef(userToken);
  useEffect(
    () => {
      userTokenRef.current = userToken;
    },
    [userToken],
  );

  // Params del BRF que en standalone llegan por URL. Van por ref por lo mismo
  // que userToken: quien dispara sync-client es el onApprove del SDK.
  const transactionParamsRef = useRef(transactionParams);
  useEffect(
    () => {
      transactionParamsRef.current = transactionParams;
    },
    [transactionParams],
  );

  const onVaultSuccessRef = useRef(onVaultSuccess);
  useEffect(
    () => {
      onVaultSuccessRef.current = onVaultSuccess;
    },
    [onVaultSuccess],
  );

  const goToScreenRef = useRef(goToScreen);
  useEffect(
    () => {
      goToScreenRef.current = goToScreen;
    },
    [goToScreen],
  );

  const searchParamRef = useRef(searchParam);
  useEffect(
    () => {
      searchParamRef.current = searchParam;
    },
    [searchParam],
  );

  const config = paypalConfig || {};

  const [sdkState, setSdkState] = useState({
    isLoading: false,
    isReady: false,
    error: null,
    clientToken: null,
    sdkInstance: null,
  });

  useEffect(() => {
    isMountedRef.current = true;

    const safeSet = patch => {
      if (isMountedRef.current) {
        setSdkState(prev => ({ ...prev, ...patch }));
      }
    };

    const initializeSDK = async () => {
      // Validar config mínima antes de continuar — sin config regional
      // no se puede inicializar el SDK. TBRF: fallback controlado.
      if (!config?.sdk?.url || !config?.sdk?.locale || !config?.sdk?.currency) {
        safeSet({
          isLoading: false,
          error: 'Configuración de PayPal no disponible',
        });
        return;
      }

      if (config?.enabled === false) {
        safeSet({
          isLoading: false,
          error: 'PayPal no está habilitado en esta región',
        });
        return;
      }

      safeSet({ isLoading: true, error: null });

      // Paso 2 — cargar el script del SDK primero (paridad con POC)
      try {
        await loadPayPalScript(config.sdk.url);
      } catch (err) {
        safeSet({
          isLoading: false,
          error: err?.message || 'No se pudo cargar el SDK de PayPal',
        });
        return;
      }

      // Paso 2b — verificar disponibilidad de createInstance
      if (typeof window.paypal?.createInstance !== 'function') {
        safeSet({
          isLoading: false,
          error: 'SDK no disponible',
        });
        return;
      }

      // Paso 3 — pedir client-token una vez que el SDK está disponible.
      // En standalone (QRLinking) se autentica con el user_token del flujo de
      // vinculación; en el flujo de Payment no se manda nada y la llamada queda
      // exactamente igual que antes.
      let clientToken;
      try {
        const response = await getPayPalClientToken(
          userId,
          standaloneRef.current ? { userToken: userTokenRef.current } : undefined,
        );
        clientToken = response?.data?.clientToken;
        if (!clientToken) {
          throw new Error('No se recibió el clientToken de PayPal');
        }
      } catch (err) {
        safeSet({
          isLoading: false,
          error: err?.message || 'No se pudo obtener el clientToken de PayPal',
        });
        return;
      }

      // Paso 4-6 — createInstance + findEligibleMethods
      try {
        const sdkInstance = await window.paypal.createInstance({
          clientToken,
          components: ['paypal-payments'],
          pageType: 'checkout',
          locale: config.sdk.locale,
        });

        await sdkInstance.findEligibleMethods({
          currencyCode: config.sdk.currency,
          paymentFlow: 'VAULT_WITHOUT_PAYMENT',
        });

        safeSet({
          isLoading: false,
          isReady: true,
          clientToken,
          sdkInstance,
        });
      } catch (err) {
        safeSet({
          isLoading: false,
          error: err?.message || 'No se pudo inicializar el SDK de PayPal',
        });
      }
    };

    initializeSDK();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Llama a sync-client tras aprobación del vault de PayPal.
  const syncPayPalVault = useCallback(async vaultSetupToken => {
    // El overlay de PayPal (login/email) vive en un iframe cross-origin que el
    // SDK remueve del DOM al aprobar: en iOS WKWebView el teclado virtual
    // puede quedar "fantasma" en pantalla porque el blur nunca llega a
    // nuestro documento (WEBCL-4092).
    dismissIOSKeyboard();

    setSdkState(prev => ({ ...prev, isLoading: true, error: null }));

    const currentPBI = currentPaymentRef.current?.currentPBI;
    const queryString = currentPBI?.linkworkflowstart?.split('?')?.[1];
    const urlParams = new URLSearchParams(queryString);

    const isSuscription =
      currentPBI?.oneoffertype === 'subscrition' ||
      currentPBI?.linkworkflowstart?.includes('object_type=A');

    const params = {
      vaultSetupToken: vaultSetupToken,
      offer_id: urlParams.get('offer_id'),
    };

    if (isSuscription) {
      params.object_type = 'A';
      params.suscription_id = urlParams.get('suscription_id');
    } else {
      params.object_type = 'G';
      params.group_id = urlParams.get('group_id');
    }

    try {
      if (standaloneRef.current) {
        const {
          object_type: objectType,
          offer_id: offerId,
          suscription_id: suscriptionId,
          group_id: groupId,
        } = transactionParamsRef.current || {};

        const standaloneParams = { vaultSetupToken };

        if (offerId) standaloneParams.offer_id = offerId;
        if (objectType) standaloneParams.object_type = objectType;

        // Igualdad estricta contra 'A'/'G' en vez del else de la rama de
        // Payment: aquí el object_type llega explícito por URL, mientras que
        // Payment lo infiere de currentPBI y puede permitirse asumir 'G' por
        // descarte. No alinear las dos ramas: no son el mismo caso.
        if (objectType === 'A' && suscriptionId) {
          standaloneParams.suscription_id = suscriptionId;
        }
        if (objectType === 'G' && groupId) {
          standaloneParams.group_id = groupId;
        }

        const syncResp = await getSyncClient(standaloneParams, {
          userToken: userTokenRef.current,
        });

        if (!syncResp) {
          throw new Error('sync-client: respuesta vacía');
        }

        // sync-client resolvió: basta con señalarlo. No se extrae ni se parsea
        // nada de la respuesta en esta rama.
        onVaultSuccessRef.current?.();
        setSdkState(prev => ({
          ...prev,
          isLoading: false,
          error: null,
        }));
        return syncResp;
      }

      const syncResp = await getSyncClient(params);

      if (!syncResp) {
        throw new Error('sync-client: respuesta vacía');
      }

      dispatch({ type: WORKFLOWSTART_SUCCESS, payload: syncResp });
      const paymentOptions = syncResp?.list || syncResp?.workflow?.listBuyLinks;
      const paypalNode = paymentOptions?.find(o => o.gateway === 'paypalgate');
      const accounts = paypalNode?.paymentMethodData?.paypalAccounts || [];
      const linkedAccount = accounts.find(a => a.isDefault) || accounts[0];

      const { currentPayment: newCurrentPayment } = getCurrentPayment(
        syncResp?.selectedPaymentMethod,
        paymentOptions,
      );

      dispatch(
        setCurrentWFS({
          ...newCurrentPayment,
          targetAccount: linkedAccount,
        }),
      );

      sessionStorage.setItem('paypal_vault_success', '1');
      goToScreenRef.current?.(
        'paypalAccountsGrid',
        '',
        searchParamRef.current?.toString(),
      );
    } catch (error) {
      setSdkState(prev => ({
        ...prev,
        isLoading: false,
        error: 'NO SE VINCULO CUENTA',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mantener la ref al día con la última versión de syncPayPalVault —
  // evita que el onApprove del SDK capture una closure stale.
  useEffect(
    () => {
      syncPayPalVaultRef.current = syncPayPalVault;
    },
    [syncPayPalVault],
  );

  // Crea la sesión de vault y retorna la saveSession del SDK.
  // Se llama UNA vez cuando el SDK está listo (isReady: true).
  // La saveSession queda en ref para que el botón pueda llamar
  // saveSession.start() directamente en el click handler.
  const startVaultSession = useCallback(
    async () => {
      if (!sdkState.sdkInstance) return null;

      if (
        typeof sdkState.sdkInstance.createPayPalSavePaymentSession !==
        'function'
      ) {
        return null;
      }

      const saveSession = sdkState.sdkInstance.createPayPalSavePaymentSession({
        commit: false, // "Continuar" en el overlay — no hay cobro inmediato
        onApprove: async data => {
          // Usar ref para evitar closure stale —
          // syncPayPalVaultRef.current siempre apunta a la versión más reciente
          if (syncPayPalVaultRef.current) {
            await syncPayPalVaultRef.current(data?.vaultSetupToken);
          }
        },
        onCancel: async () => {
          // El usuario cerró el popup — recrear la sesión para
          // permitir reintentar sin recargar la página.
          // No se muestra feedback: es una acción intencional
          // del usuario, no un error.
          dismissIOSKeyboard();
          if (startVaultSessionRef.current) {
            const newSession = await startVaultSessionRef.current();
            saveSessionRef.current = newSession;
          }
        },
        onError: err => {
          setSdkState(prev => ({
            ...prev,
            error: err?.message || 'Error en PayPal',
          }));
        },
      });

      return saveSession;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [sdkState.sdkInstance, syncPayPalVaultRef],
  );

  // Mantener la ref al día con la última versión de startVaultSession —
  // se usa desde onCancel para recrear la sesión sin closure stale.
  useEffect(
    () => {
      startVaultSessionRef.current = startVaultSession;
    },
    [startVaultSession],
  );

  // Cuando el SDK queda listo, arranca la vault session y la guarda en la ref
  useEffect(
    () => {
      if (!sdkState.isReady || !sdkState.sdkInstance) return;
      let cancelled = false;
      (async () => {
        const session = await startVaultSession();
        if (!cancelled) {
          saveSessionRef.current = session;
        }
      })();
      return () => {
        cancelled = true;
      };
    },
    [sdkState.isReady, sdkState.sdkInstance, startVaultSession],
  );

  // TODO: purchase flow — startPurchaseFlow(...) se implementará aquí
  //       (checkout SVOD/TVOD usando config.flows.svod y config.flows.tvod)

  return {
    isLoading: sdkState.isLoading,
    isReady: sdkState.isReady,
    error: sdkState.error,
    sdkInstance: sdkState.sdkInstance,
    config,
    saveSessionRef,
    syncPayPalVault, // expuesto para testing y extensibilidad futura
  };
};

export default usePayPalSDK;
